import { formatAddress, formatDate, formatPhone, maskedSsn, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)
const yes = (value) => value === true || /^(Y|YES|TRUE|1)$/.test(upper(value))
const no = (value) => value === false || /^(N|NO|FALSE|0)$/.test(upper(value))
const nameCase = (value) => titleCase(value).replace(/(^|\s)([a-z])(?=\s|$)/g, (m, space, letter) => space + letter.toUpperCase())
const personName = (n) => nameCase([n?.First, n?.Middle, n?.Last, /^(MR|MRS|MS|DR)$/i.test(text(n?.Suffix)) ? '' : n?.Suffix].filter(Boolean).join(' ') || n?.Full)
const labelCase = (value) => titleCase(text(value)).replace(/\b(Class|Level) ([a-z])\b/g, (m, word, letter) => `${word} ${letter.toUpperCase()}`)
    .replace(/\bAt&t\b/g, 'AT&T')
const yearsRange = (from, to) => [from?.Year, to?.Year].filter(Boolean).filter((y, i, all) => all.indexOf(y) === i).join('-')
const maskedLicense = (value) => (text(value).length > 4 ? `xxxx${text(value).slice(-4)}` : text(value))

const ageFrom = (dob, now) => {
    if (!dateValue(dob)) return null
    const birth = new Date(dateValue(dob))
    let age = now.getFullYear() - birth.getFullYear()
    if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age -= 1
    return age
}

export function buildPersonalInfo(person, now = new Date()) {
    const best = person?.BestInfo || {}
    const supplement = person?.BestInfoSupplement || {}
    const verified = person?.Verification?.VerifiedIndicators || {}
    const name = personName(best.Name)
    const age = Number(supplement.Age) > 0 ? Number(supplement.Age) : ageFrom(best.DOB, now)
    const deceased = yes(best.Deceased)
    const deathDate = dateValue(best.DOD) ? best.DOD : null
    const ssnInvalid = no(supplement.SSNValid)
    const confirmed = [verified.Name === true && 'name', verified.DOB === true && 'date of birth', verified.SSN === true && 'SSN'].filter(Boolean)
    const ssnNotConfirmed = verified.SSN === false

    const identities = list(person?.AKAs?.Identity).filter(i => i && typeof i === 'object')
    const aliases = [...new Set(identities.map(i => personName(i?.Name)).filter(n => n && n !== name))]

    const licenses = []
    identities.flatMap(i => list(i?.DriverLicenses?.DriverLicense)).filter(Boolean).forEach(l => {
        const key = `${upper(l.OriginState || l.Address?.State)}|${upper(l.DriverLicense)}`
        const existing = licenses.find(x => x.key === key)
        if (existing) {
            if (dateValue(l.IssueDate) > dateValue(existing.raw.IssueDate)) existing.raw = l
            return
        }
        licenses.push({ key, raw: l })
    })
    const licenseRows = licenses.map(({ raw: l }) => {
        const state = titleCase(l.OriginStateName) || l.OriginState || l.Address?.State || ''
        const commercial = /(^|[^-])COMMERCIAL|\bCDL\b/.test(`${upper(l.LicenseType)} ${upper(l.LicenseClass)}`) && !/NON-?COMMERCIAL/.test(upper(l.LicenseType))
        const cdl = text(l.CDLStatus) || (commercial ? 'Commercial' : '')
        const expired = dateValue(l.ExpirationDate) && dateValue(l.ExpirationDate) < now.getTime()
        return {
            more: moreFromLexis(l, 'AKAs/Identity[]/DriverLicenses/DriverLicense[]'),
            sortKey: dateValue(l.IssueDate) || dateValue(l.DateLastSeen),
            title: [state && `${state} driver's license`, labelCase(l.LicenseClass || l.LicenseType)].filter(Boolean).join(' · ') || "Driver's license",
            facts: [l.IssueDate && `Issued ${formatDate(l.IssueDate)}`, l.ExpirationDate && `Expires ${formatDate(l.ExpirationDate)}`,
                titleCase(l.History)].filter(Boolean).join(' · '),
            chips: [
                cdl && { label: `CDL${/^COMMERCIAL$/i.test(cdl) ? '' : `: ${titleCase(cdl)}`}`, tone: 'neutral' },
                expired && { label: 'Expired', tone: 'neutral' },
            ].filter(Boolean),
            details: [
                ['Number', maskedLicense(l.DriverLicense)],
                ['Class', [text(l.LicenseClassCode), labelCase(l.LicenseClass)].filter(Boolean).join(' · ')],
                ['Type', titleCase(l.LicenseType)],
                ['Status', titleCase(l.Status)],
                ['Commercial license status', titleCase(l.CDLStatus)],
                ['Endorsements', list(l.EndorsementsDetail?.Endorsement).map(titleCase).filter(Boolean).join('; ') || titleCase(l.Endorsements)],
                ['Restrictions', titleCase(l.Restrictions)],
                ['Original issue', formatDate(l.OrigIssueDate)],
                ['Seen', yearsRange(l.DateFirstSeen, l.DateLastSeen)],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    }).sort((a, b) => b.sortKey - a.sortKey)

    const phones = [
        ...list(person?.PhonesPluses?.PhonesPlus).filter(p => p?.PhoneNumber).map(p => ({
            sortKey: Number.MAX_SAFE_INTEGER,
            title: formatPhone(p.PhoneNumber) || text(p.PhoneNumber),
            facts: [titleCase(p.PhoneType), labelCase(p.CarrierName), p.ListedName && `Listed as ${nameCase(p.ListedName)}`].filter(Boolean).join(' · '),
            chips: [{ label: 'Current', tone: 'neutral' }],
            details: [['Listing', list(p.ListingTypes?.Type).map(titleCase).filter(Boolean).join(', ')],
                ['Carrier location', [titleCase(p.CarrierCity), p.CarrierState].filter(Boolean).join(', ')]]
                .filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        })),
        ...list(person?.OldPhones?.OldPhone).filter(p => p?.Phone10).map(p => ({
            sortKey: dateValue(p.DateLastSeen),
            title: formatPhone(p.Phone10) || text(p.Phone10),
            facts: [yearsRange(p.DateFirstSeen, p.DateLastSeen) && `Seen ${yearsRange(p.DateFirstSeen, p.DateLastSeen)}`].filter(Boolean).join(' · '),
            chips: [],
            details: [],
        })),
    ].filter((p, i, all) => all.findIndex(x => x.title === p.title) === i).sort((a, b) => b.sortKey - a.sortKey)

    const emails = [...new Map(list(person?.Emails?.Email).map(e => {
        const address = text(e?.EmailAddress || e?.OriginalEmail).toLowerCase()
        const company = [titleCase(e?.CompanyName || e?.OriginalCompanyName), titleCase(e?.CompanyTitle)].filter(Boolean).join(', ')
        return [address, company ? `${address} (${company})` : address]
    }).filter(([address]) => address)).values()]
    const bestPhone = list(best.Phones?.Phone).map(p => formatPhone(p?.Phone10)).find(Boolean) || phones[0]?.title || ''

    return {
        hasData: Boolean(name || best.SSN || best.DOB),
        name,
        facts: [
            ['Date of birth', [formatDate(best.DOB), age != null && `age ${age}`].filter(Boolean).join(', ')],
            ['SSN', [maskedSsn(best.SSN), ssnInvalid && 'not valid'].filter(Boolean).join(' · ')],
            ['Address', formatAddress(best.Address)],
            ['Phone', bestPhone],
        ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        chips: [
            deceased && { label: `Deceased${deathDate ? ` ${formatDate(deathDate)}` : ''}`, tone: 'declined' },
            !deceased && deathDate && { label: `Date of death on file (${formatDate(deathDate)})`, tone: 'watch' },
            ssnInvalid && { label: 'SSN not valid', tone: 'declined' },
            ssnNotConfirmed && { label: 'SSN not confirmed by Lexis', tone: 'watch' },
        ].filter(Boolean),
        confirmed: confirmed.length ? `Lexis confirmed ${confirmed.length === 3 ? 'name, date of birth and SSN' : confirmed.join(' and ')}` : '',
        aliases,
        licenses: licenseRows,
        phones,
        emails,
        more: [moreFromLexis(best, 'BestInfo'), moreFromLexis(supplement, 'BestInfoSupplement')].filter(Boolean),
    }
}
